"use client";

import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import type { SponsorCampaign } from "../campaignTypes";
import {
  clearPartnerSession,
  fetchPartnerCampaign,
  getStoredPartnerSession,
  storePartnerSession,
  verifyPartnerMagicToken,
} from "../partnerAuth";
import { PartnerResultsHelmet } from "../PartnerHelmet";
import { isKnownCampaignSlug } from "../loadCampaign";
import PartnerAccessGate from "../components/PartnerAccessGate";
import PartnerPassphraseGate from "../components/PartnerPassphraseGate";
import SponsorResultsPage from "../components/SponsorResultsPage";
import AmexResidencyPage from "../components/AmexResidencyPage";
import BiltResidencyPage from "../components/BiltResidencyPage";
import DeltaResidencyPage from "../components/DeltaResidencyPage";
import DraftKingsStadiumPage from "../components/DraftKingsStadiumPage";
import FoodTourPage from "../components/FoodTourPage";
import UnitedResidencyPage from "../components/UnitedResidencyPage";
import CapitalOneResidencyPage from "../components/CapitalOneResidencyPage";
import GtiPortalPage from "../components/GtiPortalPage";
import GtiFoodTourPage from "../components/GtiFoodTourPage";
import GtiResidencyPage from "../components/GtiResidencyPage";
import AirbnbPortalPage from "../components/AirbnbPortalPage";
import { portalFont, portalTokens } from "../partnerTheme";
import { hasStoredSiteAccess, usesPartnerSiteAccess } from "../partnerSiteAccess";

type LoadState =
  | { status: "booting" }
  | { status: "gated" }
  | { status: "passphrase" }
  | { status: "loading" }
  | { status: "ready"; campaign: SponsorCampaign }
  | { status: "missing" }
  | { status: "error"; message: string };

async function loadBundledCampaign(slug: string): Promise<SponsorCampaign | null> {
  if (slug === "skrewball") {
    const mod = await import("../campaigns/skrewball");
    return mod.skrewballCampaign;
  }
  if (slug === "dr-squatch") {
    const mod = await import("../campaigns/dr-squatch");
    return mod.drSquatchCampaign;
  }
  if (slug === "draft-kings") {
    const mod = await import("../campaigns/draft-kings");
    return mod.draftKingsCampaign;
  }
  if (slug === "amex-esb") {
    const mod = await import("../campaigns/amex-esb");
    return mod.amexEsbCampaign;
  }
  if (slug === "bilt-esb") {
    const mod = await import("../campaigns/bilt-esb");
    return mod.biltEsbCampaign;
  }
  if (slug === "delta-esb") {
    const mod = await import("../campaigns/delta-esb");
    return mod.deltaEsbCampaign;
  }
  if (slug === "united-esb") {
    const mod = await import("../campaigns/united-esb");
    return mod.unitedEsbCampaign;
  }
  if (slug === "capital-one-esb") {
    const mod = await import("../campaigns/capital-one-esb");
    return mod.capitalOneEsbCampaign;
  }
  if (slug === "draft-kings-stadium") {
    const mod = await import("../campaigns/draft-kings-stadium");
    return mod.draftKingsStadiumCampaign;
  }
  if (slug === "food-tour") {
    const mod = await import("../campaigns/food-tour");
    return mod.foodTourCampaign;
  }
  if (slug === "hexclad-food-tour") {
    const mod = await import("../campaigns/hexclad-food-tour");
    return mod.hexcladFoodTourCampaign;
  }
  if (slug === "gti") {
    const mod = await import("../campaigns/gti");
    return mod.gtiCampaign;
  }
  if (slug === "gti-food-tour") {
    const mod = await import("../campaigns/gti-food-tour");
    return mod.gtiFoodTourCampaign;
  }
  if (slug === "gti-residency") {
    const mod = await import("../campaigns/gti-residency");
    return mod.gtiResidencyCampaign;
  }
  if (slug === "airbnb") {
    const mod = await import("../campaigns/airbnb");
    return mod.airbnbCampaign;
  }
  return null;
}

/**
 * Partner results route: email magic-link gate, then authenticated campaign fetch.
 */
export default function PartnerCampaignSlugPage() {
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ status: "booting" });

  useEffect(() => {
    if (!campaignSlug) {
      setState({ status: "missing" });
      return;
    }
    const slug = campaignSlug;

    let cancelled = false;

    async function boot() {
      try {
      const bundled = await loadBundledCampaign(slug);
      if (cancelled) return;
      if (bundled && usesPartnerSiteAccess(slug)) {
        if (hasStoredSiteAccess(slug)) {
          setState({ status: "ready", campaign: bundled });
          return;
        }
        setState({ status: "passphrase" });
        return;
      }
      if (bundled && (slug === "gti-food-tour" || slug === "gti-residency")) {
        setState({ status: "ready", campaign: bundled });
        return;
      }
      if (import.meta.env.DEV && bundled) {
        setState({ status: "ready", campaign: bundled });
        return;
      }

      const magic = searchParams.get("magic");
      if (magic) {
        setState({ status: "loading" });
        const verified = await verifyPartnerMagicToken(magic);
        if (cancelled) return;

        if (!verified.ok || !verified.sessionToken || !verified.slug) {
          const next = new URLSearchParams(searchParams);
          next.delete("magic");
          setSearchParams(next, { replace: true });
          setState({
            status: "error",
            message: verified.error || "This access link is invalid or expired.",
          });
          return;
        }
        // Persist the session before stripping ?magic so a rerender still has access.
        storePartnerSession(verified.slug, verified.sessionToken);
        const next = new URLSearchParams(searchParams);
        next.delete("magic");
        setSearchParams(next, { replace: true });
        const loaded = await fetchPartnerCampaign(verified.slug, verified.sessionToken);
        if (cancelled) return;
        if (!loaded.ok) {
          if (loaded.status === 401 || loaded.status === 403) {
            setState({ status: "gated" });
            return;
          }
          setState({ status: "error", message: loaded.error });
          return;
        }
        setState({ status: "ready", campaign: loaded.campaign });
        return;
      }

      const existing = getStoredPartnerSession(campaignSlug);
      setState({ status: "loading" });
      const loaded = await fetchPartnerCampaign(campaignSlug, existing);
      if (cancelled) return;
      if (!loaded.ok) {
        if (loaded.status === 401 || loaded.status === 403) {
          clearPartnerSession(campaignSlug);
          setState({ status: "gated" });
          return;
        }
        if (loaded.status === 404) {
          if (isKnownCampaignSlug(campaignSlug)) {
            clearPartnerSession(campaignSlug);
            setState({ status: "gated" });
            return;
          }
          setState({ status: "missing" });
          return;
        }
        setState({ status: "error", message: loaded.error });
        return;
      }
      setState({ status: "ready", campaign: loaded.campaign });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Could not load this page.",
        });
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
    // Only re-run when slug / magic token identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignSlug, searchParams.get("magic")]);

  async function unlockBundledCampaign() {
    if (!campaignSlug) return;
    const campaign = await loadBundledCampaign(campaignSlug);
    if (campaign) {
      setState({ status: "ready", campaign });
    }
  }

  if (state.status === "booting" || state.status === "loading") {
    return (
      <Box
        sx={{
          bgcolor: portalTokens.background,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress
          sx={{
            color:
              campaignSlug === "airbnb"
                ? "#FF5A5F"
                : campaignSlug === "skrewball"
                ? "#C45C26"
                : campaignSlug === "draft-kings" || campaignSlug === "draft-kings-stadium"
                  ? "#72BB3B"
                  : campaignSlug === "amex-esb"
                    ? "#006FCF"
                    : campaignSlug === "bilt-esb" ||
                        campaignSlug === "delta-esb" ||
                        campaignSlug === "united-esb" ||
                        campaignSlug === "capital-one-esb" ||
                        campaignSlug === "food-tour" ||
                        campaignSlug === "hexclad-food-tour"
                      ? "#111111"
                      : "#2F7A3E",
          }}
        />
      </Box>
    );
  }

  if (state.status === "missing") {
    return (
      <>
        <Helmet>
          <title>Campaign not found | ComedyUO</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <Box
          component="main"
          sx={{
            bgcolor: portalTokens.background,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            py: 8,
            textAlign: "center",
          }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: portalTokens.text,
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                mb: 1.5,
              }}
            >
              Campaign not found
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                color: portalTokens.textMuted,
                mb: 3,
                maxWidth: 420,
              }}
            >
              This partner results page does not exist or the link may be incomplete.
            </Typography>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: portalTokens.text,
                textDecoration: "underline",
              }}
            >
              Back to home
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <Box
        component="main"
        sx={{
          bgcolor: portalTokens.background,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: 8,
          textAlign: "center",
        }}
      >
        <Box sx={{ maxWidth: 440 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: "1.75rem",
              mb: 1.5,
            }}
          >
            Could not open portal
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: portalTokens.textMuted, mb: 3 }}>
            {state.message}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => setState({ status: "gated" })}
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              bgcolor: "#2F7A3E",
              border: 0,
              borderRadius: "999px",
              px: 3,
              py: 1.35,
              cursor: "pointer",
            }}
          >
            Request a new link
          </Box>
        </Box>
      </Box>
    );
  }

  if (state.status === "passphrase" && campaignSlug) {
    return (
      <>
        <Helmet>
          <title>Partner portal access | ComedyUO</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <PartnerPassphraseGate slug={campaignSlug} onVerified={() => void unlockBundledCampaign()} />
      </>
    );
  }

  if (state.status === "gated" && campaignSlug) {
    return (
      <>
        <Helmet>
          <title>Partner portal access | ComedyUO</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <PartnerAccessGate
          slug={campaignSlug}
          onDebugMagicUrl={(url) => {
            // In local DEBUG API responses, jump straight to the magic link.
            if (import.meta.env.DEV) {
              window.location.assign(url);
            }
          }}
        />
      </>
    );
  }

  if (state.status === "ready") {
    const ResultsPage =
      state.campaign.campaignSlug === "amex-esb"
        ? AmexResidencyPage
        : state.campaign.campaignSlug === "bilt-esb"
          ? BiltResidencyPage
          : state.campaign.campaignSlug === "delta-esb"
            ? DeltaResidencyPage
            : state.campaign.campaignSlug === "united-esb"
              ? UnitedResidencyPage
              : state.campaign.campaignSlug === "capital-one-esb"
                ? CapitalOneResidencyPage
                : state.campaign.campaignSlug === "gti"
                  ? GtiPortalPage
                  : state.campaign.campaignSlug === "gti-food-tour"
                    ? GtiFoodTourPage
                    : state.campaign.campaignSlug === "gti-residency"
                      ? GtiResidencyPage
                      : state.campaign.campaignSlug === "airbnb"
                        ? AirbnbPortalPage
                      : state.campaign.campaignSlug === "draft-kings-stadium"
                  ? DraftKingsStadiumPage
                  : state.campaign.campaignSlug === "food-tour" ||
                      state.campaign.campaignSlug === "hexclad-food-tour"
                    ? FoodTourPage
                    : SponsorResultsPage;
    return (
      <>
        <PartnerResultsHelmet campaign={state.campaign} />
        <ResultsPage campaign={state.campaign} />
      </>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: portalTokens.background,
        minHeight: "100vh",
      }}
    />
  );
}
